import { useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import {
  Buildings,
  HouseLine,
  CurrencyEur,
  CalendarBlank,
  ArrowsClockwise,
  Warning,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { formatDate } from "shared/utils/get-dates";

interface RentalContractInfo {
  contractId?: number;
  buildingId: string;
  buildingName: string;
  unitId: string;
  unitName: string;
  phase?: string;
  currentPrice: number | null;
  originalPrice: number | null;
  startDate: string | null;
  nextIndexationDate: string | null;
}

interface ContactRentalInfo {
  rentalContracts: RentalContractInfo[];
}

interface ContactRentalInfoSectionProps {
  contactId: number;
}

const euroFormatter = new Intl.NumberFormat("nl-BE", {
  style: "currency",
  currency: "EUR",
});

const formatEuro = (price?: number | null): string =>
  price == null ? "—" : euroFormatter.format(price);

/** Returns true when the indexation date lies in the past (i.e. due / overdue). */
const isIndexationDue = (isoDate: string | null): boolean => {
  if (!isoDate) return false;
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

const ContractCard = ({
  rentalContract,
}: {
  rentalContract: RentalContractInfo;
}) => {
  const { currentPrice, originalPrice, startDate, nextIndexationDate } =
    rentalContract;

  const hasPriceIncrease =
    currentPrice != null &&
    originalPrice != null &&
    currentPrice !== originalPrice;
  const indexationDue = isIndexationDue(nextIndexationDate);

  return (
    <div className="border-neutral-primary/15 flex flex-col overflow-hidden rounded-lg border bg-white">
      {/* Header: building + unit + phase */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-100 bg-neutral-50 px-3 py-2.5">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex items-center gap-1.5">
            <Buildings
              weight="duotone"
              size={16}
              className="text-moss-primary shrink-0"
            />
            <Link
              to={`/building/${rentalContract.buildingId}`}
              className="text-moss-primary truncate text-sm font-semibold hover:underline"
            >
              {rentalContract.buildingName || rentalContract.buildingId}
            </Link>
          </div>
          <div className="flex items-center gap-1.5">
            <HouseLine
              weight="duotone"
              size={16}
              className="text-moss-primary shrink-0"
            />
            <Link
              to={`/unit/detail/${rentalContract.unitId}`}
              className="text-neutral-primary truncate text-xs font-medium hover:underline"
            >
              {rentalContract.unitName || rentalContract.unitId}
            </Link>
          </div>
        </div>
        {rentalContract.phase && (
          <span className="bg-moss-primary/10 text-moss-primary shrink-0 rounded-full px-2 py-0.5 text-xs font-medium">
            {rentalContract.phase}
          </span>
        )}
      </div>

      {/* Price block */}
      <div className="flex items-start justify-between gap-3 px-3 py-3">
        <div className="flex items-center gap-2">
          <span className="bg-moss-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
            <CurrencyEur
              weight="bold"
              size={18}
              className="text-moss-primary"
            />
          </span>
          <div className="flex flex-col">
            <span className="text-neutral-secondary-mid text-xs">
              Huidige huurprijs
            </span>
            <span className="text-neutral-primary text-lg font-bold leading-tight">
              {formatEuro(currentPrice)}
            </span>
          </div>
        </div>

        {originalPrice != null && (
          <div className="flex flex-col items-end text-right">
            <span className="text-neutral-secondary-mid text-xs">
              Oorspronkelijk
            </span>
            <span
              className={`text-sm font-medium ${
                hasPriceIncrease
                  ? "text-neutral-secondary-mid line-through"
                  : "text-neutral-primary"
              }`}
            >
              {formatEuro(originalPrice)}
            </span>
          </div>
        )}
      </div>

      {/* Dates block */}
      <div className="grid grid-cols-2 gap-px border-t border-neutral-100 bg-neutral-100">
        <div className="flex items-center gap-2 bg-white px-3 py-2.5">
          <CalendarBlank
            weight="duotone"
            size={16}
            className="text-neutral-secondary-mid shrink-0"
          />
          <div className="flex min-w-0 flex-col">
            <span className="text-neutral-secondary-mid text-xs">
              Startdatum
            </span>
            <span className="text-neutral-primary truncate text-sm font-medium">
              {startDate ? formatDate(startDate) : "—"}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-white px-3 py-2.5">
          {indexationDue ? (
            <Warning
              weight="duotone"
              size={16}
              className="shrink-0 text-amber-600"
            />
          ) : (
            <ArrowsClockwise
              weight="duotone"
              size={16}
              className="text-neutral-secondary-mid shrink-0"
            />
          )}
          <div className="flex min-w-0 flex-col">
            <span className="text-neutral-secondary-mid text-xs">
              Volgende indexatie
            </span>
            <span
              className={`truncate text-sm font-medium ${
                indexationDue ? "text-amber-700" : "text-neutral-primary"
              }`}
            >
              {nextIndexationDate ? formatDate(nextIndexationDate) : "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ContactRentalInfoSection = ({
  contactId,
}: ContactRentalInfoSectionProps) => {
  const { session } = useSession();
  const [rentalInfo, setRentalInfo] = useState<ContactRentalInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRentalInfo = async () => {
      if (!session) return;

      setIsLoading(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/contact/${contactId}/rental-info`,
          { method: "GET" },
          session,
        );

        if (!response.ok) {
          throw new Error(
            `Huurcontext ophalen mislukt (${response.status})`,
          );
        }

        setRentalInfo(await response.json());
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: "contact-detail", section: "rental-info" },
          extra: { contactId },
        });
        console.error("Error fetching contact rental info:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRentalInfo();
  }, [contactId, session]);

  if (isLoading || !rentalInfo?.rentalContracts.length) return null;

  return (
    <section className="border-neutral-primary/15 flex flex-col gap-3 rounded-md border bg-white p-4">
      <div className="flex items-center gap-2">
        <HouseLine weight="duotone" size={18} className="text-moss-primary" />
        <h3 className="text-sm font-semibold text-neutral-900">Huurcontext</h3>
        <span className="bg-moss-primary/10 text-moss-primary inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold">
          {rentalInfo.rentalContracts.length}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {rentalInfo.rentalContracts.map((rentalContract) => (
          <ContractCard
            key={
              rentalContract.contractId ??
              `${rentalContract.buildingId}-${rentalContract.unitId}`
            }
            rentalContract={rentalContract}
          />
        ))}
      </div>
    </section>
  );
};
